"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Chess, type Square } from "chess.js";
import { BoardView } from "@/components/board/BoardView";
import { MoveList } from "@/components/board/MoveList";
import type { Arrow, MoveIntent } from "@/components/board/interaction";
import { CoachPanel } from "@/components/coach/CoachPanel";
import { EvalBar } from "./EvalBar";
import { getEngine, type EngineLine } from "@/lib/chess/engine";
import { pvToSan } from "@/lib/chess/facts";
import { formatScore } from "@/lib/chess/uci";
import { START_FEN, toPgn } from "@/lib/chess/game";
import { continuations, loadOpenings, openingName, type OpeningRow } from "@/lib/chess/openingIndex";

type Node = { san: string; uci: string; from: Square; to: Square; fen: string };

/**
 * Free analysis board. Moves are a single line: playing a move from an earlier
 * position replaces everything after it. Stockfish analyses each position live.
 */
export function AnalysisClient() {
  const params = useSearchParams();
  const [startFen, setStartFen] = useState(START_FEN);
  const [line, setLine] = useState<Node[]>([]);
  const [ply, setPly] = useState(0);
  const [orientation, setOrientation] = useState<"white" | "black">("white");
  const [view, setView] = useState<"2d" | "3d">("3d");
  const [analysis, setAnalysis] = useState<{ fen: string; lines: EngineLine[]; depth: number } | null>(null);
  const [engineOn, setEngineOn] = useState(true);
  const [importText, setImportText] = useState("");
  const [importError, setImportError] = useState("");
  const [rows, setRows] = useState<OpeningRow[]>([]);
  // The rating only shapes coach requests and is never rendered, so reading it lazily is hydration-safe.
  const [rating] = useState(() => {
    if (typeof window === "undefined") return 1200;
    try {
      return Number(JSON.parse(localStorage.getItem("atlas:play") ?? "{}")?.state?.rating) || 1200;
    } catch {
      return 1200;
    }
  });

  useEffect(() => {
    void loadOpenings().then(setRows);
  }, []);

  const loadPosition = useCallback((input: string): string | null => {
    const text = input.trim();
    if (!text) return "Paste a FEN or a PGN.";
    try {
      const c = new Chess(text);
      setStartFen(c.fen());
      setLine([]);
      setPly(0);
      setOrientation(c.turn() === "w" ? "white" : "black");
      return null;
    } catch {
      /* not a FEN: try PGN */
    }
    try {
      const c = new Chess();
      c.loadPgn(text);
      const headers = c.getHeaders();
      const start = headers.FEN ?? START_FEN;
      const replayed = new Chess(start);
      const nodes: Node[] = c.history({ verbose: true }).map((m) => {
        replayed.move(m.san);
        return { san: m.san, uci: m.lan, from: m.from, to: m.to, fen: replayed.fen() };
      });
      setStartFen(start);
      setLine(nodes);
      setPly(nodes.length);
      return null;
    } catch {
      return "That is neither a valid FEN nor a valid PGN.";
    }
  }, []);

  // Positions from links such as /analysis?fen=... or ?pgn=..., applied whenever the URL changes.
  const paramKey = params.toString();
  const [appliedParams, setAppliedParams] = useState<string | null>(null);
  if (appliedParams !== paramKey) {
    setAppliedParams(paramKey);
    const f = params.get("fen");
    const p = params.get("pgn");
    if (f) loadPosition(f);
    else if (p) loadPosition(p);
  }

  const fen = ply === 0 ? startFen : line[ply - 1].fen;
  const lastMove = ply > 0 ? { from: line[ply - 1].from, to: line[ply - 1].to } : null;
  const uciSoFar = useMemo(() => line.slice(0, ply).map((n) => n.uci), [line, ply]);
  const isStandardStart = startFen === START_FEN;
  const opening = useMemo(() => (rows.length && isStandardStart ? openingName(rows, uciSoFar) : null), [rows, uciSoFar, isStandardStart]);
  const named = useMemo(() => (rows.length && isStandardStart ? continuations(rows, uciSoFar).slice(0, 6) : []), [rows, uciSoFar, isStandardStart]);
  const gameOver = useMemo(() => new Chess(fen).isGameOver(), [fen]);

  // Live engine analysis of the current position.
  useEffect(() => {
    if (!engineOn || gameOver) return;
    const ac = new AbortController();
    let frame = 0;
    const engine = getEngine();
    void engine
      .analyse(fen, {
        depth: 20,
        multipv: 3,
        signal: ac.signal,
        onUpdate: (ls, d) => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => setAnalysis({ fen, lines: ls, depth: d }));
        },
      })
      .catch(() => undefined);
    return () => {
      ac.abort();
      cancelAnimationFrame(frame);
    };
  }, [fen, engineOn, gameOver]);

  // Engine output belongs to the position it was computed for.
  const current = analysis?.fen === fen ? analysis : null;
  const lines = current?.lines ?? [];
  const depth = current?.depth ?? 0;

  const onMove = useCallback(
    (m: MoveIntent) => {
      const c = new Chess(fen);
      try {
        const mv = c.move({ from: m.from, to: m.to, promotion: m.promotion });
        const node: Node = { san: mv.san, uci: mv.lan, from: mv.from, to: mv.to, fen: c.fen() };
        // Continue the line if this move is already next in it, otherwise replace the tail.
        if (line[ply]?.uci === node.uci) setPly(ply + 1);
        else {
          setLine([...line.slice(0, ply), node]);
          setPly(ply + 1);
        }
        return true;
      } catch {
        return false;
      }
    },
    [fen, line, ply],
  );

  const playUci = (uci: string) => onMove({ from: uci.slice(0, 2) as Square, to: uci.slice(2, 4) as Square, promotion: uci[4] as MoveIntent["promotion"] });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest("input, textarea, [role=grid]")) return;
      if (e.key === "ArrowLeft") setPly((p) => Math.max(0, p - 1));
      else if (e.key === "ArrowRight") setPly((p) => Math.min(line.length, p + 1));
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [line.length]);

  const top = lines[0];
  const arrows: Arrow[] = top?.pv[0] ? [{ from: top.pv[0].slice(0, 2) as Square, to: top.pv[0].slice(2, 4) as Square }] : [];
  const blackFirst = startFen.split(" ")[1] === "b";
  const startMove = Number(startFen.split(" ")[5] ?? 1);
  const pgn = toPgn(line, { Event: "Analysis", Result: "*" }, startFen);

  return (
    <div className="mx-auto grid max-w-7xl gap-6 px-4 py-6 lg:grid-cols-[minmax(0,1fr)_400px]">
      <div className="flex min-w-0 gap-2">
        <EvalBar score={top?.score ?? null} orientation={orientation} />
        <div className="min-w-0 flex-1">
          <BoardView view={view} onViewChange={setView} fen={fen} orientation={orientation} movable="both" lastMove={lastMove} arrows={engineOn ? arrows : []} onMove={onMove} label="Analysis board" />
        </div>
      </div>

      <aside className="min-w-0 space-y-3" aria-label="Analysis panel">
        {opening && (
          <p className="text-sm">
            <span className="text-muted">{opening.eco}</span> {opening.name}
          </p>
        )}

        <section className="rounded-xl border border-border bg-surface p-3" aria-label="Engine">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base">Stockfish 19</h2>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={engineOn} onChange={(e) => setEngineOn(e.target.checked)} /> On
            </label>
          </div>
          {engineOn && (
            <>
              <p className="text-xs text-muted">{gameOver ? "Game over in this position." : `Depth ${depth}`}</p>
              <ul className="mt-2 space-y-1">
                {lines.map((l) => (
                  <li key={l.multipv} className="flex gap-2 text-sm">
                    <span className="w-12 shrink-0 font-mono font-semibold">{formatScore(l.score)}</span>
                    <button className="min-w-0 truncate text-left font-mono text-muted hover:text-text" onClick={() => l.pv[0] && playUci(l.pv[0])} title="Play the first move of this line">
                      {pvToSan(fen, l.pv, 10).join(" ")}
                    </button>
                  </li>
                ))}
                {/* Keep room for all three lines so the panel does not grow as the engine starts. */}
                {Array.from({ length: Math.max(0, 3 - lines.length) }, (_, i) => <li key={`slot${i}`} className="text-sm" aria-hidden="true">{" "}</li>)}
              </ul>
            </>
          )}
        </section>

        {!rows.length && isStandardStart && (
          // Same shape as the list below, held open while the opening catalogue downloads.
          <section className="rounded-xl border border-border bg-surface p-3" aria-label="Named continuations" aria-busy="true">
            <h2 className="font-display text-base">Book moves</h2>
            <p className="text-xs text-muted">Named lines from the Lichess opening catalogue.</p>
            <ul className="mt-2 space-y-1 text-sm" aria-hidden="true">{Array.from({ length: 6 }, (_, i) => <li key={i}>{" "}</li>)}</ul>
          </section>
        )}
        {named.length > 0 && (
          <section className="rounded-xl border border-border bg-surface p-3" aria-label="Named continuations">
            <h2 className="font-display text-base">Book moves</h2>
            <p className="text-xs text-muted">Named lines from the Lichess opening catalogue.</p>
            <ul className="mt-2 space-y-1 text-sm">
              {named.map((n) => (
                <li key={n.uci}>
                  <button className="text-left hover:underline" onClick={() => playUci(n.uci)}>
                    <span className="font-mono font-semibold">{pvToSan(fen, [n.uci])[0]}</span> <span className="text-muted">{n.names[0]}{n.lines > 1 ? ` and ${n.lines - 1} more` : ""}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <MoveList moves={line} current={ply} onSelect={setPly} blackFirst={blackFirst} startMove={startMove} />
        <div className="flex flex-wrap gap-1">
          {[
            ["«", "Start", () => setPly(0)],
            ["‹", "Back", () => setPly(Math.max(0, ply - 1))],
            ["›", "Forward", () => setPly(Math.min(line.length, ply + 1))],
            ["»", "End", () => setPly(line.length)],
          ].map(([t, l, f]) => (
            <button key={l as string} aria-label={l as string} onClick={f as () => void} className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm hover:bg-surface-2">
              {t as string}
            </button>
          ))}
          <button onClick={() => setOrientation((o) => (o === "white" ? "black" : "white"))} className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm hover:bg-surface-2">
            Flip
          </button>
          <button onClick={() => setView((v) => (v === "2d" ? "3d" : "2d"))} className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm hover:bg-surface-2">
            {view === "2d" ? "3D board" : "2D board"}
          </button>
          <Link href={`/tools/chess-diagram?fen=${encodeURIComponent(fen)}`} className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm hover:bg-surface-2">
            Make a diagram
          </Link>
        </div>

        <CoachPanel
          label="Explain this position"
          request={{
            mode: "position",
            fen,
            rating,
            openingName: opening?.name,
            engine: top ? { before: { score: top.score, pv: top.pv.slice(0, 10) } } : undefined,
          }}
        />

        <details className="rounded-xl border border-border bg-surface p-3 text-sm">
          <summary className="cursor-pointer font-medium">Import FEN or PGN</summary>
          <textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder="Paste a FEN or a PGN"
            className="mt-2 h-24 w-full rounded-lg border border-border bg-bg p-2 font-mono text-xs"
          />
          <button
            onClick={() => {
              const err = loadPosition(importText);
              setImportError(err ?? "");
            }}
            className="mt-2 rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-accent-ink"
          >
            Load
          </button>
          {importError && <p className="mt-1 text-danger">{importError}</p>}
          <p className="mt-3 text-xs text-muted">Current FEN</p>
          <input readOnly value={fen} className="w-full rounded border border-border bg-bg px-2 py-1 font-mono text-xs" onFocus={(e) => e.target.select()} />
          <p className="mt-2 text-xs text-muted">PGN</p>
          <textarea readOnly value={pgn} className="h-20 w-full rounded border border-border bg-bg p-2 font-mono text-xs" />
        </details>
      </aside>
    </div>
  );
}
